import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import coinbase from 'assets/wallet/icon-coinbase.png';
import ledger from 'assets/wallet/icon-ledger.png';
import metamask from 'assets/wallet/icon-metamask.png';
import opera from 'assets/wallet/icon-opera.png';
import phantom from 'assets/wallet/icon-phantom.svg';
import trustWallet from 'assets/wallet/icon-trustWallet.svg';
import walletConnect from 'assets/wallet/icon-walletConnect.png';
import ButtonsList, { ButtonProps } from 'atoms/buttons/ButtonsList';
import LoadingIndicator from 'atoms/loader/LoadingIndicator';
import { Text16 } from 'atoms/typography';
import { HOST, ORIGIN } from 'config';
import { useBlockchainContext } from 'contexts/blockchain';
import useScreenSize from 'hooks/device/useScreenSize';
import useToggle from 'hooks/useToggle';
import useEphemeralLink from 'hooks/users/useEphemeralLink';

const StyledImg = styled.img`
  max-width: var(--quadruple-unit);
  max-height: var(--quadruple-unit);
`;
const ButtonText = styled.div`
  text-align: center;
  width: 100%;
`;

const RecommendedWalletApp = () => {
  const { up: isLaptop } = useScreenSize('laptop');
  const { setupWalletConnect, setupCoinbaseWallet, setupLedger } =
    useBlockchainContext();
  const [showMoreOptions, toggleShowMoreOptions] = useToggle(false);
  const [encodedRedirectUrl, setEncodedRedirectUrl] = useState<
    string | undefined
  >(undefined);
  const createEphemeralLink = useEphemeralLink();
  const [loading, setIsLoading] = useState(false);

  if (!encodedRedirectUrl && !loading) {
    setIsLoading(true);
    const url = `${ORIGIN}?action=addFundsEth`;
    createEphemeralLink(url)
      .then(link => {
        if (link) {
          setEncodedRedirectUrl(encodeURI(link));
        } else {
          setEncodedRedirectUrl(encodeURI(url));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  if (!encodedRedirectUrl) return <LoadingIndicator />;

  const METAMASK_MOBILE_APP_LINK = `https://metamask.app.link/dapp/${HOST}?action=addFundsEth`;
  const COINBASE_MOBILE_APP_LINK = `https://go.cb-w.com/dapp?cb_url=${encodedRedirectUrl}`;
  const TRUST_WALLET_MOBILE_APP_LINK = `https://link.trustwallet.com/open_url?coin_id=60&url=${encodedRedirectUrl}`;

  const mobileButtons = (ButtonComponent: ButtonProps) => (
    <>
      <ButtonComponent href={METAMASK_MOBILE_APP_LINK}>
        <StyledImg alt="Metamask" src={metamask} />
        <Text16>Metamask</Text16>
      </ButtonComponent>
      <ButtonComponent href={COINBASE_MOBILE_APP_LINK}>
        <StyledImg alt="coinbase" src={coinbase} />
        <Text16>Coinbase Wallet</Text16>
      </ButtonComponent>
      <ButtonComponent href={TRUST_WALLET_MOBILE_APP_LINK}>
        <StyledImg alt="Trust Wallet" src={trustWallet} />
        <Text16>Trust Wallet</Text16>
      </ButtonComponent>
      {showMoreOptions && (
        <>
          <ButtonComponent onClick={setupWalletConnect}>
            <StyledImg alt="Wallet Connect" src={walletConnect} />
            <Text16>Wallet Connect</Text16>
          </ButtonComponent>
          <ButtonComponent onClick={setupLedger}>
            <StyledImg alt="Ledger" src={ledger} />
            <Text16>Ledger</Text16>
          </ButtonComponent>
          <ButtonComponent href="https://www.opera.com/fr/crypto">
            <StyledImg alt="opera" src={opera} />
            <Text16>Opera</Text16>
          </ButtonComponent>
        </>
      )}
    </>
  );

  const desktopButtons = (ButtonComponent: ButtonProps) => (
    <>
      <ButtonComponent href="https://metamask.io/">
        <StyledImg alt="Metamask" src={metamask} />
        <Text16>Metamask</Text16>
      </ButtonComponent>
      <ButtonComponent onClick={setupCoinbaseWallet}>
        <StyledImg alt="Coinbase" src={coinbase} />
        <Text16>Coinbase Wallet</Text16>
      </ButtonComponent>
      <ButtonComponent href="https://trustwallet.com/browser-extension">
        <StyledImg alt="Trust Wallet" src={trustWallet} />
        <Text16>Trust Wallet</Text16>
      </ButtonComponent>
      <ButtonComponent href="https://phantom.app/download">
        <StyledImg alt="Phantom" src={phantom} />
        <Text16>Phantom</Text16>
      </ButtonComponent>
      {showMoreOptions && (
        <>
          <ButtonComponent onClick={setupWalletConnect}>
            <StyledImg alt="Wallet Connect" src={walletConnect} />
            <Text16>Wallet Connect</Text16>
          </ButtonComponent>
          <ButtonComponent onClick={setupLedger}>
            <StyledImg alt="Ledger" src={ledger} />
            <Text16>Ledger</Text16>
          </ButtonComponent>
          <ButtonComponent href="https://www.opera.com/fr/crypto">
            <StyledImg alt="opera" src={opera} />
            <Text16>Opera</Text16>
          </ButtonComponent>
        </>
      )}
    </>
  );

  return (
    <ButtonsList>
      {ButtonComponent => (
        <>
          {isLaptop
            ? desktopButtons(ButtonComponent)
            : mobileButtons(ButtonComponent)}

          <ButtonComponent onClick={toggleShowMoreOptions}>
            <ButtonText>
              <Text16>
                {showMoreOptions ? (
                  <FormattedMessage
                    id="RecommendedWalletApp.collapse"
                    defaultMessage="Show less"
                  />
                ) : (
                  <FormattedMessage
                    id="RecommendedWalletApp.collapsed"
                    defaultMessage="Show more"
                  />
                )}
              </Text16>
            </ButtonText>
          </ButtonComponent>
        </>
      )}
    </ButtonsList>
  );
};

export default RecommendedWalletApp;
